import type { ChatMessage } from '@/services/chat/chatTransport';
import type { FetchMessagesOptions } from '@/services/chat/pollingChatTransport';
import type { paths } from '@/types/api';
import { fetchClient } from '@/util/fetchClient';

/**
 * Reads the full message list of an inquiry's conversation from our own backend,
 * mapping each row to the seam's {@link ChatMessage}. This is what the polling
 * chat transport fetches on each tick to derive realtime events, so it exists
 * only to serve the test-gated polling path — production chat reads messages
 * through Twilio, not this endpoint.
 *
 * The endpoint is participant-authorized (the `read`/`Inquiry` CASL ability) and
 * returns the full message shape including `index` and `dateUpdated`, which the
 * admin-only conversations route omits.
 */
type InquiryMessageDto = NonNullable<
  paths['/v2/inquiries/{conversationId}/messages']['get']['responses']['200']['content']['application/json']['data']
>[number];

function parseAttributes(raw: unknown): unknown {
  if (raw == null) {
    return {};
  }

  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }

  return raw;
}

function toChatMessage(dto: InquiryMessageDto): ChatMessage {
  return {
    id: dto.sid,
    index: dto.index,
    author: dto.author,
    body: dto.body,
    attributes: parseAttributes(dto.attributes),
    createdAt: dto.dateCreated ? new Date(dto.dateCreated) : null,
    updatedAt: dto.dateUpdated ? new Date(dto.dateUpdated) : null,
  };
}

/** With `after`, reads only the messages past that index. */
export async function getInquiryMessages(
  conversationId: string,
  options: FetchMessagesOptions = {}
): Promise<ChatMessage[]> {
  const { data } = await fetchClient.GET('/v2/inquiries/{conversationId}/messages', {
    params: {
      path: { conversationId },
      ...(options.after === undefined ? {} : { query: { after: options.after } }),
    },
  });

  return (data?.data ?? []).map(toChatMessage);
}
